export type FeedbackAction =
    | "AssignToRGM"
    | "ContactRGMNow"
    | "EmailRGM"
    | "AddToFollowUp"
    | "AlreadyAware"
    | "NotActionable"
    | "ProvideFeedback";

export enum InsightActionStatus {
    Open = "Open",
    InProgress = "InProgress",
    Resolved = "Resolved",
    NotImproved = "NotImproved"
}

export interface IInsightAction {
    id: string;
    insightKey: string;
    locationName: string;
    locationPk: number;
    businessDate: string;
    dayPart: string;
    title: string;
    category: string;
    actionType: FeedbackAction;
    note: string;
    status: InsightActionStatus;
    createdAt: string;
    estimatedRoi: number;
    averageSOS: string;
    targetSOS: string;
    impactedExperiences: number;
    timeframeStartHour: number;
    timeframeEndHour: number;
    whoToFollowUpWith: string[] | null;
    reviewedAt: string | null;
    reviewOutcome: "Improved" | "NoChange" | "Worse" | null;
    selectedActionId?: string | null;
}

export interface ISpeedOfServiceInsight {
    locationName: string;
    locationPk: number;
    locNbr: number;
    businessDate: string;
    dayPart: string;
    title: string;
    category: string;
    averageSOS: string;
    targetSOS: string;
    impactedExperiences: number;
    whatHappened: string;
    whyThisMatters: string;
    whoToFollowUpWith: string[] | null;
    timeframeStartHour: number;
    timeframeEndHour: number;
    recommendedAction: string;
    suggestedFeedbackAction: FeedbackAction;
    estimatedRoi: number;
}

export interface ICoachInsightsResponse {
    message?: string;
    driveThruTarget: string;
    speedOfServiceInsights: ISpeedOfServiceInsight[];
}

export enum MatchStatus {
    Matched = "Matched",
    SosOnly = "SosOnly",
    TransactionOnly = "TransactionOnly"
}

export interface IImpactedExperience {
    cascadeTrigger: string;
    date: string;
    hour: string;
    secondOfDay: number;
    exitTime: number | null;
    cashOutTime: number | null;
    total: string;
    menu: string;
    queue: string;
    window: string;
    ticketNbr: number | null;
    orderNumber: string | null;
    empName: string | null;
    chanCode: string | null;
    chanDesc: string | null;
    matchStatus: MatchStatus;
    transactionTotal: number | null;
    hireDt: string | null;
}

export interface IImpactedExperiencesResponse {
    totalCount: number;
    poorExperienceCount: number;
    transactions: IImpactedExperience[];
}

export interface IStaffCoverageMember {
    name: string;
    jobRole: string;
    clockIn: string;
    clockOut: string;
    hours: number;
    tenure: string;
}

export interface IStaffCoverageResponse {
    staffMembers: IStaffCoverageMember[];
}

export interface ILaborScheduleShift {
    employeeId: string;
    employeeName: string;
    jobDescription: string;
    busDt: string;
    clockIn: string;
    clockOut: string;
}

export interface ILaborScheduleResponse {
    shifts: ILaborScheduleShift[];
}

export interface ILaborStaffingCandidate {
    employeeId: string;
    employeeName: string;
    /** "available" | "unavailable" | "empty" for the target day — never "shift". */
    currentStatus: string;
    note?: string;
    existingRole?: string;
    weeklyScheduledHours: number;
}

export interface ILaborStaffingSuggestionRequest {
    locationName: string;
    dayLabel: string;
    windowLabel: string;
    roleNeeded: string;
    candidates: ILaborStaffingCandidate[];
}

export interface ILaborStaffingSuggestionResponse {
    hasSuggestion: boolean;
    employeeId?: string;
    employeeName?: string;
    startTime?: string;
    endTime?: string;
    role?: string;
    reason?: string;
}

export interface ISosBusinessImpactHourlyData {
    hour: number;
    driveThruSales: number;
    driveThruTransactions: number;
    carsOverThreshold: number;
    driveThruTotalTimeSec: number;
    menuTimeSec: number;
    queueTimeSec: number;
    serviceTimeSec: number;
}

export interface ISosBusinessImpactResponse {
    hourlyData: ISosBusinessImpactHourlyData[];
    wowHourlyData: ISosBusinessImpactHourlyData[];
    yoyHourlyData: ISosBusinessImpactHourlyData[];
    thresholdSeconds: number;
    menuThresholdSeconds: number;
    queueThresholdSeconds: number;
    serviceThresholdSeconds: number;
}
